import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  OutputDataObjectGroupProcessingStateType,
  DataObjectType,
  DropdownItem,
  getGroupDescriptionRef,
  Tooltip,
  useSystemClassifierItemsBy,
} from '@atlas-frontend/sdk';
import { Button, Dropdown, Flex } from 'antd';
import { FC, useMemo, memo, useCallback } from 'react';
import translations from './OutputDataActionCell.translations.json';
import {
  getRowIdAndType,
  useNavigateToRelationsWithLatestVersion,
} from '../input-data/utils';
import * as Icon from 'react-feather';
import { CellContext } from '@tanstack/react-table';
import { OutputDataAction, OutputDataActionType } from './OutputDataDirectory';
import { useNavigateToDescriptionWithLatestActiveVersion } from './useNavigateToDescriptionWithLatestActiveVersion';
import { path } from '../../paths';
import { withParams } from '../../paths/withParams';

const { addTranslations, t } = i18nNsFactory('output-data-action-cell');
addTranslations(translations);

export const OutputDataActionCell: FC<
  CellContext<OutputDataObjectGroupProcessingStateType, any> & {
    onAction: (action: OutputDataAction) => void;
    onDropdownOpenChange?: (open: boolean) => void;
  }
> = memo((props) => {
  const { onAction, onDropdownOpenChange } = props;

  const processingState = props.row.original;

  const {
    dataObjectGroupId,
    standaloneDataObjectDescriptionId,
    outputTypeId,
    frequencyId,
    latestDataObjectVersionId,
    hasAccess,
  } = processingState;
  const { id, name, type, valueDate } = useMemo(
    () => getRowIdAndType(processingState),
    [processingState],
  );

  const { OUTPUT_TYPE: outputTypesById } = useSystemClassifierItemsBy(
    ['OUTPUT_TYPE'],
    'id',
  );
  const outputTypeCode =
    outputTypeId != null ? outputTypesById.get(outputTypeId)?.code : undefined;

  const isRunning =
    processingState.processing?.phaseCode === 'PENDING' ||
    processingState.processing?.phaseCode === 'ACTIVE';

  const navigateToRelationsWithLatestVersion =
    useNavigateToRelationsWithLatestVersion(
      outputTypeCode || 'OUTPUT',
      valueDate,
      dataObjectGroupId,
      standaloneDataObjectDescriptionId,
    );
  const navigateToDescriptionWithLatestActiveVersion =
    useNavigateToDescriptionWithLatestActiveVersion(
      standaloneDataObjectDescriptionId,
      valueDate,
    );

  const navigateToCrossTable = useCallback(() => {
    const url = withParams(path.visualValidationCrossTable, {
      groupDescriptionRef: getGroupDescriptionRef(
        dataObjectGroupId,
        standaloneDataObjectDescriptionId,
      ),
      valueDate,
      version: latestDataObjectVersionId || undefined,
    });
    window.open(url, '_blank');
  }, [
    standaloneDataObjectDescriptionId,
    dataObjectGroupId,
    latestDataObjectVersionId,
    valueDate,
  ]);

  const handleActionClick = useCallback(
    (actionKey: OutputDataActionType | 'openRelationsTable') => {
      if (actionKey === 'openRelationsTable') {
        onAction({
          type: actionKey,
          params: {
            groupOrDescriptionId: getGroupDescriptionRef(
              dataObjectGroupId,
              standaloneDataObjectDescriptionId,
            ),
            valueDate,
            version: latestDataObjectVersionId ?? 'latest',
            dataObjectType: DataObjectType.OUT,
            isGroup: !standaloneDataObjectDescriptionId,
            rootNodeName: name || '',
          },
        });
        return;
      }
      if (!dataObjectGroupId || !frequencyId) return;
      onAction({
        type: actionKey,
        params: {
          groupId: dataObjectGroupId,
          valueDate,
          frequencyId,
          hasAccess,
        },
      });
    },
    [
      standaloneDataObjectDescriptionId,
      dataObjectGroupId,
      frequencyId,
      latestDataObjectVersionId,
      name,
      onAction,
      hasAccess,
      valueDate,
    ],
  );

  const menu = useMemo(
    () => ({
      items: [
        {
          label: <DropdownItem>{t('actions.executeCalculation')}</DropdownItem>,
          key: 'executeCalculation',
          disabled: isRunning,
          onClick: () => handleActionClick('calculate'),
        },
        {
          label: <DropdownItem>{t('actions.validate')}</DropdownItem>,
          key: 'validate',
          disabled: isRunning,
          onClick: () => handleActionClick('validate'),
        },
        {
          label: (
            <DropdownItem>{t('actions.calculateAndValidate')}</DropdownItem>
          ),
          key: 'calculateAndValidate',
          disabled: isRunning,
          onClick: () => handleActionClick('calculateAndValidate'),
        },
        {
          label: (
            <DropdownItem>{t('actions.cancelCalculationErrors')}</DropdownItem>
          ),
          key: 'cancelCalculationErrors',
          disabled: isRunning,
          onClick: () => handleActionClick('cancelCalculationErrors'),
        },
        { type: 'divider' as const },
        {
          label: <DropdownItem>{t('actions.confirm')}</DropdownItem>,
          key: 'confirm',
          onClick: () => handleActionClick('confirm'),
        },
        {
          label: (
            <DropdownItem>{t('actions.confirmAndCalculate')}</DropdownItem>
          ),
          key: 'confirmAndCalculate',
          onClick: () => handleActionClick('confirmAndCalculate'),
        },
        {
          label: (
            <DropdownItem>{t('actions.changeConfirmStatus')}</DropdownItem>
          ),
          key: 'changeConfirmStatus',
          onClick: () => handleActionClick('changeConfirmStatus'),
        },
        { type: 'divider' as const },
        {
          label: (
            <Tooltip
              title={
                !standaloneDataObjectDescriptionId &&
                t('tooltip.showOutputData.groupedOutput')
              }
            >
              <DropdownItem>{t('actions.showOutputData')}</DropdownItem>
            </Tooltip>
          ),
          key: 'showOutputData',
          disabled: !standaloneDataObjectDescriptionId,
          onClick: () => navigateToCrossTable(),
        },
        {
          label: (
            <Tooltip
              title={
                !standaloneDataObjectDescriptionId &&
                t('tooltip.showDescription.groupedOutput')
              }
            >
              <DropdownItem>{t('actions.showDescription')}</DropdownItem>
            </Tooltip>
          ),
          key: 'showDescription',
          disabled: !standaloneDataObjectDescriptionId,
          onClick: () => navigateToDescriptionWithLatestActiveVersion(),
        },
        { type: 'divider' as const },
        {
          label: <DropdownItem>{t('actions.relations')}</DropdownItem>,
          key: 'relations',
          onClick: () => navigateToRelationsWithLatestVersion(),
        },
        {
          label: <DropdownItem>{t('actions.relationsTables')}</DropdownItem>,
          key: 'relationsTables',
          onClick: () => handleActionClick('openRelationsTable'),
        },
        {
          label: <DropdownItem>{t('actions.showRelated')}</DropdownItem>,
          key: 'showRelated',
          onClick: () => handleActionClick('showRelated'),
        },
        {
          label: <DropdownItem>{t('actions.showConfirmations')}</DropdownItem>,
          key: 'showConfirmations',
          onClick: () => handleActionClick('showConfirmations'),
        },
        { type: 'divider' as const },
        {
          label: <DropdownItem>{t('actions.delete')}</DropdownItem>,
          key: 'delete',
          onClick: () => handleActionClick('delete'),
        },
      ],
    }),
    [
      isRunning,
      standaloneDataObjectDescriptionId,
      handleActionClick,
      navigateToCrossTable,
      navigateToDescriptionWithLatestActiveVersion,
      navigateToRelationsWithLatestVersion,
    ],
  );

  return (
    <Flex justify="end">
      <Dropdown
        menu={menu}
        trigger={['click']}
        placement="bottomRight"
        onOpenChange={onDropdownOpenChange}
      >
        <Button
          id={`output-action-${type}-${id}`}
          type="text"
          shape="circle"
          icon={<Icon.MoreVertical size={20} />}
          size="small"
        />
      </Dropdown>
    </Flex>
  );
});
